/**
 * NewsletterForm — GDPR consent validation + Klaviyo list subscription.
 *
 * Behaviour:
 *  - If consent checkbox is present and unchecked → blocks submit, shows error.
 *  - If `klaviyo_company_id` + `klaviyo_list_id` are both configured →
 *      intercepts the native Shopify form, calls Klaviyo Subscribe API v3,
 *      shows inline success / error.
 *  - If Klaviyo is not configured → lets the Shopify form submit normally
 *      (Shopify–Klaviyo integration handles the sync via webhook).
 *
 * Config is passed from Liquid via a <script type="application/json"> child element.
 */
class NewsletterForm extends HTMLElement {
  constructor() {
    super();
    this.config = {};
    this.form = null;
    this.consentCheckbox = null;
    this.consentError = null;
    this.formError = null;
    this.submitButton = null;
    this._handleSubmit = this.handleSubmit.bind(this);
    this._handleConsentChange = this.handleConsentChange.bind(this);
  }

  connectedCallback() {
    const dataEl = this.querySelector('script[type="application/json"]');
    if (dataEl) {
      try {
        this.config = JSON.parse(dataEl.textContent);
      } catch {
        this.config = {};
      }
    }

    this.form = this.querySelector('form');
    this.consentCheckbox = this.querySelector('.email-signup__consent-checkbox');
    this.consentError = this.querySelector('.email-signup__consent-error');
    this.formError = this.querySelector('.email-signup__klaviyo-error');
    this.submitButton = this.querySelector('.email-signup__button');

    // Clear consent error when checkbox is toggled
    if (this.consentCheckbox) {
      this.consentCheckbox.addEventListener('change', this._handleConsentChange);
    }

    // Listen at document level in capture phase so our handler runs before
    // any Shopify form listeners, preventing their navigation from firing.
    if (this.form) {
      document.addEventListener('submit', this._handleSubmit, { capture: true });
    }
  }

  disconnectedCallback() {
    if (this.consentCheckbox) {
      this.consentCheckbox.removeEventListener('change', this._handleConsentChange);
    }
    document.removeEventListener('submit', this._handleSubmit, { capture: true });
  }

  handleConsentChange() {
    // Clear the error when the user checks the box
    if (this.consentCheckbox.checked) this.hideConsentError();
  }

  handleSubmit(event) {
    // Only handle our own form (document-level listener catches all forms)
    if (event.target !== this.form) return;

    // 1. Validate consent checkbox first
    if (this.consentCheckbox && !this.consentCheckbox.checked) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showConsentError(this.config.error_consent);
      this.consentCheckbox.focus();
      return;
    }

    // 2. Klaviyo mode — intercept and call API directly
    const { klaviyo_company_id, klaviyo_list_id } = this.config;
    if (klaviyo_company_id && klaviyo_list_id) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const emailInput = this.form.querySelector('input[type="email"]');
      if (emailInput) {
        this.subscribeToKlaviyo(
          emailInput.value.trim(),
          klaviyo_company_id,
          klaviyo_list_id
        );
      }
    }
    // No Klaviyo → let event propagate for Shopify native form submit
  }

  async subscribeToKlaviyo(email, companyId, listId) {
    this.setLoading(true);
    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'revision': '2024-10-15'
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email }
                  }
                }
              },
              relationships: {
                list: {
                  data: { type: 'list', id: listId }
                }
              }
            }
          })
        }
      );

      // Klaviyo returns 202 Accepted on success (empty body)
      if (response.ok || response.status === 202) {
        this.showSuccess();
      } else {
        this.showError(this.config.error_klaviyo);
      }
    } catch {
      this.showError(this.config.error_klaviyo);
    } finally {
      this.setLoading(false);
    }
  }

  showSuccess() {
    // Hide the input group and consent section
    const inputGroup = this.querySelector('.email-signup__input-group');
    const consent = this.querySelector('.email-signup__consent');
    if (inputGroup) inputGroup.hidden = true;
    if (consent) consent.hidden = true;

    // Reveal the always-rendered success message div
    const successMsg = this.querySelector('.email-signup__message--success');
    if (successMsg) {
      successMsg.removeAttribute('hidden');
      successMsg.focus();
    }
  }

  showConsentError(message) {
    if (!this.consentError) return;
    this.consentError.textContent = message;
    this.consentError.hidden = false;
  }

  hideConsentError() {
    if (!this.consentError) return;
    this.consentError.hidden = true;
    this.consentError.textContent = '';
  }

  showError(message) {
    // Use the dedicated form error element (near the button); fall back to
    // consent error if the newer element is not present in the DOM.
    const el = this.formError || this.consentError;
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  setLoading(loading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = loading;
    this.submitButton.setAttribute('aria-busy', loading ? 'true' : 'false');
  }
}

if (!customElements.get('newsletter-form')) {
  customElements.define('newsletter-form', NewsletterForm);
}
